// @see https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
import { useCallback, useEffect, useRef } from "react";
import { getWindow } from "../helpers/getWindow";

export const useIntersectionObserver = (
    {
        threshold = 0,
        root = null,
        rootMargin = undefined,
        freezeOnceVisible = false,
        freezeOnceInvisible = false,
        axis = "both",
    }: IntersectionObserverInit & {
        freezeOnceVisible?: boolean;
        freezeOnceInvisible?: boolean;
        axis?: "horizontal" | "vertical" | "both";
    },
    onIntersection: ((entry: IntersectionObserverEntry) => void) | undefined
): ((element: Element | null) => void) => {
    const window = getWindow();
    const observerRef = useRef<IntersectionObserver | null>(null);
    const elementRef = useRef<Element | null>(null);
    const onIntersectionRef = useRef(onIntersection);

    const effectiveRootMargin = rootMargin
        ? rootMargin
        : axis === "horizontal"
          ? "10000px 0px"
          : axis === "vertical"
            ? "0px 10000px"
            : "0%";

    useEffect(() => {
        onIntersectionRef.current = onIntersection;
    }, [onIntersection]);

    useEffect(() => {
        if (!window || !window.IntersectionObserver) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry === undefined) {
                    return;
                }

                if (freezeOnceVisible && entry.isIntersecting) {
                    observer.disconnect();
                }

                if (freezeOnceInvisible && !entry.isIntersecting) {
                    observer.disconnect();
                }

                onIntersectionRef.current?.(entry);
            },
            {
                threshold,
                root,
                rootMargin: effectiveRootMargin,
            }
        );

        observerRef.current = observer;

        if (elementRef.current) {
            observer.observe(elementRef.current);
        }

        return (): void => {
            observer.disconnect();
            observerRef.current = null;
        };
    }, [
        window,
        threshold,
        root,
        effectiveRootMargin,
        freezeOnceVisible,
        freezeOnceInvisible,
    ]);

    return useCallback((element: Element | null): void => {
        elementRef.current = element;

        if (observerRef.current) {
            observerRef.current.disconnect();

            if (element) {
                observerRef.current.observe(element);
            }
        }
    }, []);
};
